import React, { useState } from "react";
import axiosInstance from "../../axiosInstance";
import "./LaunchedTemplates.scss";

/**
 * Review & save form for a launched template (from /launched-notifications
 * or /launched-automations), turned into a live scheduled event / cron job.
 *
 * Button logic (as specified):
 * - item.alreadyUsed === true  -> single "Save" button, saves directly with
 *   the item's existing id (no popup).
 * - item.alreadyUsed === false -> single "Update" button, which opens a
 *   confirm popup: "Duplicate" (create new -> id sent as null) or
 *   "Overwrite" (update existing -> id sent as-is).
 *
 * status is no longer editable in the UI - always sent as "Enabled".
 *
 * fromEmailSource and toEmailSourceFrom are two DIFFERENT backend enums
 * (confirmed from separate 500 errors) - do not merge these lists.
 */

const TO_EMAIL_SOURCE_OPTIONS = [
  "RESPECTIVE_MANAGER",
  "RESPECTIVE_INTERVIEWER",
  "BRANCH",
  "ALL_MEMBERS_IN_PROJECT",
  "RESPECTIVE_ACCOUNTANT",
  "RESPECTIVE_CONSULTANT",
  "RESPECTIVE_LEAD_ASSIGNED_EMPLOYEE",
  "RESPECTIVE_RECRUITER_HR",
  "RESPECTIVE_PROJECT_MEMBER",
  "RESPECTIVE_HOD",
  "RESPECTIVE_INTERVIEWERS",
  "RESPECTIVE_OPERATION_STAGE_MEMBERS",
  "RESPECTIVE_STAGE_TASK_ASSIGNED_MEMBERS",
  "RESPECTIVE_TASK_ASSIGNED_MEMBERS",
  "ROLE",
  "RESPECTIVE_HR",
  "SPECIFIC_EMPLOYEES",
  "RESPECTIVE_EMPLOYEE",
  "COMPANY",
  "CANDIDATE",
  "ALLEMP",
  "DEPARTMENT",
  "RESPECTIVE_CLIENT_ASSIGNED_EMPLOYEE",
  "RESPECTIVE_VACANCY_CREATOR",
  "AUTHORITY",
  "ALL_RESPECTIVE_NEXT_ROUND_INTERVIEWERS",
];

const FROM_EMAIL_SOURCE_OPTIONS = [
  "RESPECTIVE_MANAGER",
  "RESPECTIVE_INTERVIEWER",
  "CANDIDATE",
  "RESPECTIVE_ACCOUNTANT",
  "RESPECTIVE_RECRUITER_HR",
  "RESPECTIVE_SALES_MANAGER",
  "RESPECTIVE_HOD",
  "SPECIFIC",
  "RESPECTIVE_PRODUCTION_MANAGER",
  "RESPECTIVE_HR",
  "TOOL",
  "RESPECTIVE_EMPLOYEE",
  "COMPANY",
];

// Confirmed from a 500 error.
const TRIGGER_DATE_OPTIONS = ["OnDATE", "DateAFTER", "DateBEFORE"];

const STATUS_SENT_TO_BACKEND = "ENABLED";

const emptySourceRow = () => ({ id: 0, sourceId: "", employeeId: "" });
const emptyEmailRow = () => ({ id: 0, email: "", employeeId: "" });

// Best-effort mapping from legacy free-text values (e.g. "Respective Employee")
// to the real enum constant (e.g. "RESPECTIVE_EMPLOYEE"). Falls back to "" if
// nothing matches, so the admin has to consciously pick the right value.
const normalizeEnumValue = (raw, options) => {
  if (!raw) return "";
  const norm = raw.toString().trim().toUpperCase().replace(/\s+/g, "_");
  if (options.includes(norm)) return norm;
  const fuzzy = options.find((o) => o.startsWith(norm) || norm.startsWith(o));
  return fuzzy || "";
};

// TriggerDate uses mixed casing ("OnDATE", "DateAFTER", "DateBEFORE") rather
// than SCREAMING_SNAKE_CASE, so match case-insensitively instead of reusing
// normalizeEnumValue's underscore normalization.
const matchEnumCaseInsensitive = (raw, options) => {
  if (!raw) return "";
  const found = options.find(
    (o) => o.toLowerCase() === raw.toString().trim().toLowerCase(),
  );
  return found || "";
};

// cronJobExpression is 6 space-separated fields: seconds minute hour day month weekday
// e.g. "* 0 8 * * *" = every day at 08:00. We only expose hour/minute for editing;
// the rest stay as "*" to match the pattern seen in existing data.
const parseCronTime = (expr) => {
  if (!expr) return { hour: "8", minute: "0" };
  const parts = expr.split(" ");
  return { minute: parts[1] ?? "0", hour: parts[2] ?? "8" };
};

const buildCronExpression = (hour, minute) => `* ${minute} ${hour} * * *`;

const LaunchedTemplateReviewForm = ({ item, type, onClose, onSaved }) => {
  const isEvent = type === "event";
  const template = isEvent ? item.eventTemplate : item.cronJobTemplate;

  const [taskName, setTaskName] = useState(item.taskName || "");

  const [toEmailSourceFrom, setToEmailSourceFrom] = useState(
    normalizeEnumValue(item.toEmailSourceFrom, TO_EMAIL_SOURCE_OPTIONS),
  );
  const [fromEmailSource, setFromEmailSource] = useState(
    normalizeEnumValue(item.fromEmailSource, FROM_EMAIL_SOURCE_OPTIONS),
  );
  const toEmailSourceMatched = !!normalizeEnumValue(
    item.toEmailSourceFrom,
    TO_EMAIL_SOURCE_OPTIONS,
  );
  const fromEmailSourceMatched = !!normalizeEnumValue(
    item.fromEmailSource,
    FROM_EMAIL_SOURCE_OPTIONS,
  );

  const [fromEmail, setFromEmail] = useState(item.fromEmail || "");
  const [employeeId, setEmployeeId] = useState(item.employeeId || "");
  const [companyId, setCompanyId] = useState(item.companyId || "");

  // Cron-job-only fields
  const initialCronTime = parseCronTime(item.cronJobExpression);
  const [cronHour, setCronHour] = useState(initialCronTime.hour);
  const [cronMinute, setCronMinute] = useState(initialCronTime.minute);
  const [triggerDate, setTriggerDate] = useState(
    matchEnumCaseInsensitive(item.triggerDate, TRIGGER_DATE_OPTIONS),
  );
  const triggerDateMatched = !!matchEnumCaseInsensitive(
    item.triggerDate,
    TRIGGER_DATE_OPTIONS,
  );
  const [days, setDays] = useState(item.days ?? "");
  const [templateStatus, setTemplateStatus] = useState(
    item.templateStatus || "CUSTOM",
  );

  const [selectedNotificationTypeIds, setSelectedNotificationTypeIds] =
    useState((item.notificationTypes || []).map((n) => n.notificationTypeId));

  const [templateFields, setTemplateFields] = useState({
    templateId: template?.templateId || 0,
    templateName: template?.templateName || "",
    subject: template?.subject || "",
    body: template?.body || "",
    htmlBody: template?.htmlBody ?? false,
    notificationHeading: template?.notificationHeading || "",
    notificationMessage: template?.notificationMessage || "",
  });

  const [notifyToSource, setNotifyToSource] = useState([]);
  const [notifyToEmails, setNotifyToEmails] = useState([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);

  const toggleNotificationType = (id) => {
    setSelectedNotificationTypeIds((prev) =>
      prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id],
    );
  };

  const updateTemplateField = (field, value) => {
    setTemplateFields((prev) => ({ ...prev, [field]: value }));
  };

  const updateRow = (rows, setRows, index, field, value) => {
    setRows(
      rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  };

  const idField = isEvent ? "scheduledEventId" : "scheduledCronJobId";

  const doSave = async (idOverride) => {
    setSaving(true);
    setError(null);
    try {
      const baseNotifyToSource = notifyToSource.map((row) => ({
        id: row.id || 0,
        sourceId: Number(row.sourceId) || 0,
        employeeId: Number(row.employeeId) || 0,
      }));
      const baseNotifyToEmails = notifyToEmails.map((row) => ({
        id: row.id || 0,
        email: row.email,
        employeeId: Number(row.employeeId) || 0,
      }));

      if (isEvent) {
        const payload = {
          scheduledEventId: idOverride,
          taskName,
          eventTypeId: item.eventTypeId,
          notificationTypeIds: selectedNotificationTypeIds,
          toEmailSourceFrom,
          fromEmailSource,
          fromEmail,
          status: STATUS_SENT_TO_BACKEND,
          employeeId: Number(employeeId) || 0,
          companyId: Number(companyId) || 0,
          eventTemplate: templateFields,
          notifyToSource: baseNotifyToSource,
          notifyToEmails: baseNotifyToEmails,
        };
        await axiosInstance.post(
          `${process.env.REACT_APP_API_BASE_URL}/scheduled-events/save`,
          payload,
        );
      } else {
        const payload = {
          scheduledCronJobId: idOverride,
          cronJobTypeId: item.cronJobTypeId,
          taskName,
          notificationTypeIds: selectedNotificationTypeIds,
          toEmailSourceFrom,
          fromEmailSource,
          fromEmail,
          cronJobExpression: buildCronExpression(cronHour, cronMinute),
          triggerDate,
          days: Number(days) || 0,
          status: STATUS_SENT_TO_BACKEND,
          employeeId: Number(employeeId) || 0,
          companyId: Number(companyId) || 0,
          cronJobTemplate: templateFields,
          notifyToSource: baseNotifyToSource,
          notifyToEmails: baseNotifyToEmails,
          templateCode: item.templateCode,
          templateStatus,
        };
        await axiosInstance.post(
          `${process.env.REACT_APP_API_BASE_URL}/scheduled-cron-jobs/save`,
          payload,
        );
      }
      onSaved();
    } catch (err) {
      console.error("Error saving launched template:", err);
      setError("Failed to save. Please check the fields and try again.");
    } finally {
      setSaving(false);
      setShowUpdateConfirm(false);
    }
  };

  // alreadyUsed === true -> direct save, keep the existing id.
  const handleDirectSave = () => doSave(item[idField] || 0);

  // alreadyUsed === false -> ask duplicate (id: null) vs overwrite (id: existing).
  const handleDuplicate = () => doSave(null);
  const handleOverwrite = () => doSave(item[idField] || 0);

  const canSubmit =
    toEmailSourceFrom && fromEmailSource && (isEvent || triggerDate);

  return (
    <div className="launched-template-review-overlay">
      <div className="launched-template-review-modal">
        <div className="review-header">
          <h2>
            Review &amp; {item.alreadyUsed ? "save" : "update"}{" "}
            {isEvent ? "notification" : "automation"}
          </h2>
          <button className="close-button" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        {error && <p className="error-message">{error}</p>}

        <div className="review-body">
          <div className="field-row">
            <label>Task name</label>
            <input
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
            />
          </div>

          <div className="field-row two-col">
            <div>
              <label>From email source</label>
              <select
                value={fromEmailSource}
                onChange={(e) => setFromEmailSource(e.target.value)}
              >
                <option value="">Select...</option>
                {FROM_EMAIL_SOURCE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              {!fromEmailSourceMatched && item.fromEmailSource && (
                <span className="field-hint warning">
                  Previous value "{item.fromEmailSource}" didn't match an enum -
                  please confirm.
                </span>
              )}
            </div>
            <div>
              <label>To email source</label>
              <select
                value={toEmailSourceFrom}
                onChange={(e) => setToEmailSourceFrom(e.target.value)}
              >
                <option value="">Select...</option>
                {TO_EMAIL_SOURCE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              {!toEmailSourceMatched && item.toEmailSourceFrom && (
                <span className="field-hint warning">
                  Previous value "{item.toEmailSourceFrom}" didn't match an enum
                  - please confirm.
                </span>
              )}
            </div>
          </div>

          <div className="field-row two-col">
            <div>
              <label>From email</label>
              <input
                value={fromEmail}
                onChange={(e) => setFromEmail(e.target.value)}
              />
            </div>
            <div>
              <label>Employee ID</label>
              <input
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
              />
            </div>
          </div>

          <div className="field-row">
            <label>Company ID</label>
            <input
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
            />
          </div>

          {!isEvent && (
            <>
              <div className="field-row two-col">
                <div>
                  <label>Time (hour : minute)</label>
                  <div className="time-picker">
                    <select
                      value={cronHour}
                      onChange={(e) => setCronHour(e.target.value)}
                    >
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={h}>
                          {String(h).padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                    <span>:</span>
                    <select
                      value={cronMinute}
                      onChange={(e) => setCronMinute(e.target.value)}
                    >
                      {Array.from({ length: 60 }, (_, m) => (
                        <option key={m} value={m}>
                          {String(m).padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                  </div>
                  <span className="field-hint">
                    Sends as: {buildCronExpression(cronHour, cronMinute)}
                  </span>
                </div>
                <div>
                  <label>Trigger date</label>
                  <select
                    value={triggerDate}
                    onChange={(e) => setTriggerDate(e.target.value)}
                  >
                    <option value="">Select...</option>
                    {TRIGGER_DATE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                  {!triggerDateMatched && item.triggerDate && (
                    <span className="field-hint warning">
                      Previous value "{item.triggerDate}" didn't match an enum -
                      please confirm.
                    </span>
                  )}
                </div>
              </div>
              <div className="field-row two-col">
                <div>
                  <label>Days</label>
                  <input
                    value={days}
                    onChange={(e) => setDays(e.target.value)}
                  />
                </div>
                <div>
                  <label>Template status</label>
                  <input
                    value={templateStatus}
                    onChange={(e) => setTemplateStatus(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          <div className="field-row">
            <label>Notification types</label>
            <div className="notification-type-checkboxes">
              {(item.notificationTypes || []).map((n) => (
                <label key={n.notificationTypeId} className="checkbox-pill">
                  <input
                    type="checkbox"
                    checked={selectedNotificationTypeIds.includes(
                      n.notificationTypeId,
                    )}
                    onChange={() =>
                      toggleNotificationType(n.notificationTypeId)
                    }
                  />
                  {n.notificationType}
                </label>
              ))}
              {(!item.notificationTypes ||
                item.notificationTypes.length === 0) && (
                <span className="empty-hint">
                  No notification types on this template.
                </span>
              )}
            </div>
          </div>

          <fieldset className="template-fieldset">
            <legend>Template</legend>
            <div className="field-row">
              <label>Subject</label>
              <input
                value={templateFields.subject}
                onChange={(e) => updateTemplateField("subject", e.target.value)}
              />
            </div>
            <div className="field-row">
              <label>Notification heading</label>
              <input
                value={templateFields.notificationHeading}
                onChange={(e) =>
                  updateTemplateField("notificationHeading", e.target.value)
                }
              />
            </div>
            <div className="field-row">
              <label>Notification message</label>
              <input
                value={templateFields.notificationMessage}
                onChange={(e) =>
                  updateTemplateField("notificationMessage", e.target.value)
                }
              />
            </div>
            <div className="field-row">
              <label>Body</label>
              <textarea
                rows={6}
                value={templateFields.body}
                onChange={(e) => updateTemplateField("body", e.target.value)}
              />
            </div>
            <label className="checkbox-pill">
              <input
                type="checkbox"
                checked={templateFields.htmlBody}
                onChange={(e) =>
                  updateTemplateField("htmlBody", e.target.checked)
                }
              />
              HTML body
            </label>
          </fieldset>

          <fieldset className="template-fieldset">
            <legend>Notify — email sources</legend>
            {notifyToSource.map((row, index) => (
              <div className="field-row two-col dynamic-row" key={index}>
                <input
                  placeholder="Source ID"
                  value={row.sourceId}
                  onChange={(e) =>
                    updateRow(
                      notifyToSource,
                      setNotifyToSource,
                      index,
                      "sourceId",
                      e.target.value,
                    )
                  }
                />
                <input
                  placeholder="Employee ID"
                  value={row.employeeId}
                  onChange={(e) =>
                    updateRow(
                      notifyToSource,
                      setNotifyToSource,
                      index,
                      "employeeId",
                      e.target.value,
                    )
                  }
                />
                <button
                  type="button"
                  className="remove-row-button"
                  onClick={() =>
                    setNotifyToSource(
                      notifyToSource.filter((_, i) => i !== index),
                    )
                  }
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="add-row-button"
              onClick={() =>
                setNotifyToSource([...notifyToSource, emptySourceRow()])
              }
            >
              + Add source
            </button>
          </fieldset>

          <fieldset className="template-fieldset">
            <legend>Notify — direct emails</legend>
            {notifyToEmails.map((row, index) => (
              <div className="field-row two-col dynamic-row" key={index}>
                <input
                  placeholder="Email"
                  value={row.email}
                  onChange={(e) =>
                    updateRow(
                      notifyToEmails,
                      setNotifyToEmails,
                      index,
                      "email",
                      e.target.value,
                    )
                  }
                />
                <input
                  placeholder="Employee ID"
                  value={row.employeeId}
                  onChange={(e) =>
                    updateRow(
                      notifyToEmails,
                      setNotifyToEmails,
                      index,
                      "employeeId",
                      e.target.value,
                    )
                  }
                />
                <button
                  type="button"
                  className="remove-row-button"
                  onClick={() =>
                    setNotifyToEmails(
                      notifyToEmails.filter((_, i) => i !== index),
                    )
                  }
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="add-row-button"
              onClick={() =>
                setNotifyToEmails([...notifyToEmails, emptyEmailRow()])
              }
            >
              + Add email
            </button>
          </fieldset>
        </div>

        <div className="review-footer">
          <button
            className="secondary-button"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          {item.alreadyUsed ? (
            <button
              className="primary-button"
              onClick={handleDirectSave}
              disabled={saving || !canSubmit}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          ) : (
            <button
              className="primary-button"
              onClick={() => setShowUpdateConfirm(true)}
              disabled={saving || !canSubmit}
            >
              Update
            </button>
          )}
        </div>

        {showUpdateConfirm && (
          <div className="update-confirm-overlay">
            <div className="update-confirm-box">
              <h3>This template came from a different application.</h3>
              <p>
                Do you want to create a new entry, or overwrite the existing
                one?
              </p>
              <div className="update-confirm-actions">
                <button
                  className="secondary-button"
                  onClick={() => setShowUpdateConfirm(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="secondary-button"
                  onClick={handleDuplicate}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Duplicate (new)"}
                </button>
                <button
                  className="primary-button"
                  onClick={handleOverwrite}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Overwrite existing"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LaunchedTemplateReviewForm;
